import React from 'react';
import { Typography } from '@klp/design';
import type { Theme } from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import type { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useCurrentUser } from '@klp/auth';
import classNames from 'classnames';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import type { LeverandorGodkjenningProsessResponse } from '../types';
import { useMapEmailToUser } from '../hooks/useMapEmailToUser';
import { LeverandorRoutes } from '../config';
import { MenuPopper } from './MenuPopper/MenuPopper';
import { formatDate, parseSafeDate } from '@klp/utils/dateTimeFormatter';
import { FilterTreff } from '@klp/design/FilterPanel/FilterPanel';
import { visVerdi } from '@klp/utils/tableUtils';
import { useKolonnevalg } from '@forvalter/utils/useKolonnevalg';
import { listetabellStiler } from '@forvalter/utils/listetabellStiler';

type LeverandorTableProps = {
    leverandorer: Array<LeverandorGodkjenningProsessResponse>;
};

// Remembers the sorting between visits. A new name, so an «Ansvarlig» filter
// stored by the old «Tildelt meg» link doesn't stay. The chosen columns are
// kept by useKolonnevalg, since mui-datatables doesn't keep them for columns
// with a default.
const LEVERANDOR_TABLE_STORAGE_KEY = 'leverandorTableStateV2';
const LEVERANDOR_TABLE_KOLONNER_KEY = 'leverandorTableKolonner';

// Hidden until the user shows them under «Vis kolonner»
const STANDARD_SKJULTE_KOLONNER = ['fullfortDato'];

// Define column metadata that's independent of runtime data
// This serves as the single source of truth for column configuration
const LEVERANDOR_TABLE_COLUMN_METADATA = [
    { name: 'orgnummer', label: 'leverandor.table.orgnummer' },
    {
        name: 'd365Leverandornummer',
        label: 'leverandor.table.d365Leverandornummer',
    },
    { name: 'navn', label: 'leverandor.table.navn' },
    { name: 'type', label: 'leverandor.table.type' },
    { name: 'registreringStatus', label: 'leverandor.table.status' },
    { name: 'aktivitet', label: 'leverandor.table.aktivitet' },
    { name: 'bestiller', label: 'leverandor.table.bestiller' },
    { name: 'okonomi', label: 'leverandor.table.okonomi' },
    { name: 'ansvarlig', label: 'leverandor.table.ansvarlig' },
    {
        name: 'opprettetAv',
        label: 'leverandor.table.opprettetAv',
        hidden: true,
    },
    { name: 'startDato', label: 'leverandor.table.startDato' },
    { name: 'fullfortDato', label: 'leverandor.table.fullfortDato' },
    { name: 'action', label: 'leverandor.table.action' },
] as const;

// Use metadata as single source of truth for column names and labels
const COL = LEVERANDOR_TABLE_COLUMN_METADATA;

const styles = (theme: Theme) => ({
    centerText: {
        textAlign: 'center',
    },
    table: {
        cursor: 'pointer',
        ...listetabellStiler(theme),
    },
    tableBlurred: {
        // Elements, since MUI's class names get a generated suffix in this app
        '& tbody tr': {
            opacity: 0.4,
            filter: 'blur(1px)',
            transition: 'opacity 0.2s, filter 0.2s',
            pointerEvents: 'none',
        },
        '& tbody tr[data-active="true"]': {
            opacity: 1,
            filter: 'none',
            pointerEvents: 'auto',
            backgroundColor: theme.palette.secondary.veryLight,
        },
    },
});

type KolonneParams = {
    t: TFunction;
    leverandorer: Array<LeverandorGodkjenningProsessResponse>;
    handleMapping: ReturnType<typeof useMapEmailToUser>['handleMapping'];
    isEDAdmin?: boolean;
    openPopperId: string | null;
    anchorEl: HTMLElement | null;
    handleTogglePopper: (
        prosessInstansId: string,
    ) => (event: React.MouseEvent<HTMLButtonElement>) => void;
    handleClosePopper: () => void;
};

/** The table's columns, before the user's column choices are applied. */
const getKolonner = ({
    t,
    leverandorer,
    handleMapping,
    isEDAdmin,
    openPopperId,
    anchorEl,
    handleTogglePopper,
    handleClosePopper,
}: KolonneParams): Array<MUIDataTableColumn> => [
    // COL[0] - orgnummer
    {
        label: t(COL[0].label),
        name: COL[0].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(leverandorer[dataIndex]?.orgnummer)}
                </Typography>
            ),
        },
    },
    // COL[1] - d365Leverandornummer
    {
        label: t(COL[1].label),
        name: COL[1].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(leverandorer[dataIndex]?.d365Leverandornummer)}
                </Typography>
            ),
        },
    },
    // COL[2] - navn
    {
        label: t(COL[2].label),
        name: COL[2].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(leverandorer[dataIndex]?.navn)}
                </Typography>
            ),
        },
    },
    // COL[3] - type
    {
        label: t(COL[3].label),
        name: COL[3].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {t(
                        `leverandor.createLeverandor.type.${leverandorer[dataIndex]?.type}`,
                    )}
                </Typography>
            ),
        },
    },
    // COL[4] - status
    {
        label: t(COL[4].label),
        name: COL[4].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {t(
                        `leverandor.statuses.${leverandorer[dataIndex]?.registreringStatus}`,
                    )}
                </Typography>
            ),
        },
    },
    // COL[5] - aktivitet
    {
        label: t(COL[5].label),
        name: COL[5].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(leverandorer[dataIndex]?.currentAktivitet?.navn)}
                </Typography>
            ),
        },
    },
    // COL[6] - bestiller
    {
        label: t(COL[6].label),
        name: COL[6].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(leverandorer[dataIndex]?.bestillerFulltnavn)}
                </Typography>
            ),
        },
    },
    // COL[7] - okonomi
    {
        label: t(COL[7].label),
        name: COL[7].name,
        options: {
            display: 'excluded',
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(leverandorer[dataIndex]?.okonomiFulltnavn)}
                </Typography>
            ),
        },
    },
    // COL[8] - ansvarlig
    {
        label: t(COL[8].label),
        name: COL[8].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(
                        handleMapping(
                            leverandorer[dataIndex]?.currentAktivitet
                                ?.tildeltTil,
                        ),
                    )}
                </Typography>
            ),
        },
    },
    // COL[9] - opprettetAv (HIDDEN)
    {
        label: t(COL[9].label),
        name: COL[9].name,
        options: {
            display: 'excluded',
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(leverandorer[dataIndex]?.opprettetAv)}
                </Typography>
            ),
        },
    },
    // COL[10] - startDato
    {
        label: t(COL[10].label),
        name: COL[10].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(
                        formatDate(
                            parseSafeDate(leverandorer[dataIndex]?.startDato),
                        ),
                    )}
                </Typography>
            ),
        },
    },
    // COL[11] - fullfortDato
    {
        label: t(COL[11].label),
        name: COL[11].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(
                        formatDate(
                            parseSafeDate(
                                leverandorer[dataIndex]?.fullfortDato,
                            ),
                        ),
                    )}
                </Typography>
            ),
        },
    },
    // COL[12] - action
    {
        label: t(COL[12].label),
        name: COL[12].name,
        options: {
            sort: false,
            customBodyRenderLite: (dataIndex) => {
                const leverandor = leverandorer[dataIndex];

                if (!leverandor) {
                    return null;
                }

                return (
                    <MenuPopper
                        leverandor={leverandor}
                        isEDAdmin={isEDAdmin}
                        open={openPopperId === leverandor.prosessInstansId}
                        anchorEl={anchorEl}
                        onToggle={handleTogglePopper(
                            leverandor.prosessInstansId,
                        )}
                        onClose={handleClosePopper}
                    />
                );
            },
        },
    },
];

type TabellvalgParams = {
    t: TFunction;
    leverandorer: Array<LeverandorGodkjenningProsessResponse>;
    // The row with an open menu, which stays clear while the rest is blurred
    openPopperId: string | null;
    onRowClick: NonNullable<MUIDataTableOptions['onRowClick']>;
    onViewColumnsChange: NonNullable<
        MUIDataTableOptions['onViewColumnsChange']
    >;
};

const getTabellvalg = ({
    t,
    leverandorer,
    openPopperId,
    onRowClick,
    onViewColumnsChange,
}: TabellvalgParams): MUIDataTableOptions => ({
    onRowClick,
    onViewColumnsChange,
    // Searching and filtering is done in the filter panel above the table
    search: false,
    download: false,
    print: false,
    viewColumns: true,
    // Filtering is done in the filter panel above the table
    filter: false,
    sort: true,
    selectableRows: 'none',
    storageKey: LEVERANDOR_TABLE_STORAGE_KEY,
    textLabels: {
        body: {
            noMatch: t('leverandor.table.emptyTableMessage'),
            toolTip: t('tables.body.toolTip'),
            columnHeaderTooltip: (column) =>
                t('tables.body.toolTipFor') + column.label,
        },
        pagination: {
            next: t('tables.pagination.next'),
            previous: t('tables.pagination.previous'),
            rowsPerPage: t('tables.pagination.rowsPerPage'),
            displayRows: t('tables.pagination.displayRows'),
        },
        toolbar: {
            downloadCsv: t('tables.toolbar.downloadCsv'),
            print: t('tables.toolbar.print'),
            viewColumns: t('tables.toolbar.viewColumns'),
        },
        viewColumns: {
            title: t('tables.viewColumns.title'),
            titleAria: t('tables.viewColumns.titleAria'),
        },
        selectedRows: {
            text: t('tables.selectedRows.text'),
            delete: t('tables.selectedRows.delete'),
            deleteAria: t('tables.selectedRows.deleteAria'),
        },
    },
    setRowProps: (row, dataIndex) => ({
        'data-active':
            openPopperId === leverandorer[dataIndex]?.prosessInstansId
                ? 'true'
                : 'false',
    }),
});

export const LeverandorTable: React.FC<LeverandorTableProps> = ({
    leverandorer,
}) => {
    const classes = useKlpStyles({}, styles);
    const { t } = useTranslation();
    const { currentUser } = useCurrentUser();
    const history = useHistory();
    const { handleMapping } = useMapEmailToUser();
    const { medKolonnevalg, onViewColumnsChange } = useKolonnevalg(
        LEVERANDOR_TABLE_KOLONNER_KEY,
        STANDARD_SKJULTE_KOLONNER,
    );

    const handleRowClick = React.useCallback(
        (
            rowData: Array<string>,
            rowMeta: { dataIndex: number; rowIndex: number },
        ) => {
            const prosessInstansId =
                leverandorer[rowMeta.dataIndex]?.prosessInstansId;
            if (!prosessInstansId) {
                return;
            }

            history.push(
                LeverandorRoutes.leverandorSchemaSingle(prosessInstansId),
            );
        },
        [leverandorer, history],
    );

    const [openPopperId, setOpenPopperId] = React.useState<string | null>(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const handleTogglePopper = React.useCallback(
        (prosessInstansId: string) =>
            (event: React.MouseEvent<HTMLButtonElement>) => {
                if (openPopperId === prosessInstansId) {
                    setOpenPopperId(null);
                    setAnchorEl(null);
                } else {
                    setOpenPopperId(prosessInstansId);
                    setAnchorEl(event.currentTarget);
                }
            },
        [openPopperId],
    );

    const handleClosePopper = React.useCallback(() => {
        setOpenPopperId(null);
        setAnchorEl(null);
    }, []);

    // Flattened so MuiDataTable can sort on ansvarlig/aktivitet
    const flattenedLeverandorer = React.useMemo(
        () =>
            leverandorer.map((leverandor) => ({
                ...leverandor,
                ansvarlig: leverandor.currentAktivitet?.tildeltTil,
                aktivitet: leverandor.currentAktivitet?.navn,
            })),
        [leverandorer],
    );

    // Memoized so the table doesn't rebuild its columns on every render
    const tableColumns = React.useMemo(
        () =>
            medKolonnevalg(
                getKolonner({
                    t,
                    leverandorer,
                    handleMapping,
                    isEDAdmin: currentUser?.isEDAdmin,
                    openPopperId,
                    anchorEl,
                    handleTogglePopper,
                    handleClosePopper,
                }),
            ),
        [
            medKolonnevalg,
            t,
            leverandorer,
            handleMapping,
            currentUser?.isEDAdmin,
            openPopperId,
            anchorEl,
            handleTogglePopper,
            handleClosePopper,
        ],
    );

    const tableOptions = React.useMemo(
        () =>
            getTabellvalg({
                t,
                leverandorer,
                openPopperId,
                onRowClick: handleRowClick,
                onViewColumnsChange,
            }),
        [t, leverandorer, openPopperId, handleRowClick, onViewColumnsChange],
    );

    return (
        <>
            <div
                className={classNames(classes.table, {
                    [classes.tableBlurred]: openPopperId,
                })}
            >
                <MUIDataTable
                    title={<FilterTreff antall={leverandorer.length} />}
                    data={flattenedLeverandorer}
                    columns={tableColumns}
                    options={tableOptions}
                    data-testid="LeverandorTable__Table"
                />
            </div>
        </>
    );
};
